import { PostSetStatus } from '@cf-shared/enums';
import { LL } from 'i18n/i18n.service';
import { TranslatedString } from 'models/translated-string';

// Why this tiny module exists: concludeSet in workout-detail.ts asks the user WHY a rep test ran at
// a weight far from the prescribed one, and the answer decides which message the user reads back.
// The decision is pure, but concludeSet around it is not (see workout-progression-decision.ts's
// header for the same reasoning), so it lives here where a unit test can reach it — #4558.

/** What the user answered when asked why the rep test used a different weight than prescribed. */
export enum RepTestWeightAnswer {
  // "the weight you're recommending is way too heavy"
  TooHeavy = 'too_heavy',
  // "the weight you're recommending is way too light"
  TooLight = 'too_light',
  // "my chosen weight is the closest I can get to what you're telling me to use"
  ClosestAvailable = 'closest_available',
}

/**
 * Which direction the user means when the rep test ran at a weight far from the prescribed one.
 * Their own weight choice says it: picking LESS than prescribed means "that is too heavy for me",
 * picking more means "that is too light". Only called once the two weights are known to differ, so
 * the alert can offer that one answer instead of both and never show the impossible one (#4558).
 *
 * This reads what the user BELIEVES about the prescription, not how the set went: a set logged
 * lighter than prescribed still says "too heavy" here even when its rep count says the load was
 * easy. Those are two different statements, and the answer button is about the first one.
 */
export function repTestWeightAnswerForLoggedWeight(loggedWeight_kg: number, prescribedWeight_kg: number): RepTestWeightAnswer {
  // if the user reached for more weight than we asked for
  if (loggedWeight_kg > prescribedWeight_kg) {
    return RepTestWeightAnswer.TooLight;
  }
  return RepTestWeightAnswer.TooHeavy;
}

/**
 * The post-set status to record for the user's answer. It picks the message the set shows and the
 * line the coach email carries, so it must name the direction the user actually chose: before
 * #4558 a single answer covered both directions and everyone was told they had said "too heavy".
 */
export function postSetStatusForRepTestWeightAnswer(answer: RepTestWeightAnswer): PostSetStatus {
  // a switch over every answer, so adding one is a compile error here rather than a silent
  // "too heavy" — which is the mismatch this module exists to prevent
  switch (answer) {
    case RepTestWeightAnswer.TooHeavy:
      return PostSetStatus.RepTestFailToHeavy;
    case RepTestWeightAnswer.TooLight:
      return PostSetStatus.RepTestFailToLight;
    case RepTestWeightAnswer.ClosestAvailable:
      return PostSetStatus.RepTestBadEquipment;
  }
}

/**
 * The answer a recorded post-set status says the user gave, or `undefined` for a status that
 * records something else.
 *
 * It is DERIVED from `postSetStatusForRepTestWeightAnswer` rather than restating the same table
 * backwards, because a second copy of the table is a copy somebody can forget: an answer added
 * to the enum and mapped forward, but missed here, would read back as "nobody answered" — and on
 * a correction that means the set is judged a failure and loses the progression the user's answer
 * earned, which is the very bug this function exists to prevent (df-6c87ae2df081). Derived, an
 * added answer is read back without anybody having to remember this function at all.
 *
 * It is what lets a correction judge the weight the way the first conclusion did — the status is
 * the only account of the answer that survives, because a correction asks the user nothing.
 */
export function repTestWeightAnswerForPostStatus(status: PostSetStatus | undefined): RepTestWeightAnswer | undefined {
  // a set carrying no answer is the ordinary case, and no status maps to it
  if (status === undefined) {
    return undefined;
  }
  return Object.values(RepTestWeightAnswer).find(answer => postSetStatusForRepTestWeightAnswer(answer) === status);
}

/**
 * The text of the answer button for one direction. It lives here, beside the status the same
 * answer records, so a unit test can prove the two agree — wiring the button text to one direction
 * and the recorded status to the other would recreate the very bug #4558 is about.
 */
export function alertTextForRepTestWeightAnswer(answer: RepTestWeightAnswer): TranslatedString {
  switch (answer) {
    case RepTestWeightAnswer.TooHeavy:
      return LL.workouts.alerts.weightTooHeavy();
    case RepTestWeightAnswer.TooLight:
      return LL.workouts.alerts.weightTooLight();
    case RepTestWeightAnswer.ClosestAvailable:
      return LL.workouts.alerts.closestWeight();
  }
}

/**
 * How far a rep test's logged weight may sit from the prescribed one before the app asks why.
 * A couple of percent covers ordinary equipment rounding.
 */
const REP_TEST_WEIGHT_TOLERANCE = 0.02;

/**
 * Whether a rep test ran far enough from the prescribed weight that the app should ask why.
 * Both the question (asked before the set is concluded) and the progression that follows it call
 * this, so they cannot end up applying two different bands.
 */
export function isRepTestWeightOffTarget(loggedWeight_kg: number, prescribedWeight_kg: number): boolean {
  // a path with no target yet prescribes nothing, so nothing can be on target — the answer this
  // gave before the check was pulled out of the page, and the safe one: it keeps the set out of the
  // success path rather than crediting a rep test against a weight the app never asked for
  if (typeof prescribedWeight_kg !== 'number' || !isFinite(prescribedWeight_kg)) {
    return true;
  }
  // differently signed weights are never close: a negative weight is assistance, a positive one load
  if (loggedWeight_kg * prescribedWeight_kg < 0) {
    return true;
  }
  const logged = Math.abs(loggedWeight_kg);
  const prescribed = Math.abs(prescribedWeight_kg);
  return Math.abs(logged - prescribed) > Math.max(logged, prescribed) * REP_TEST_WEIGHT_TOLERANCE;
}

/** Whether to ask the user why a rep test ran at this weight, and which answer to offer. */
export interface RepTestWeightQuestion {
  /** whether the logged weight is far enough off the prescribed one to be worth asking about */
  weightWasOffTarget: boolean;
  /** whether there is a real prescribed weight to ask ABOUT — see `decideRepTestWeightQuestion` */
  canAsk: boolean;
  /** the one direction to offer, which the user's own weight choice implies. Only meaningful
   *  when `canAsk` is true, because without a prescribed weight there is no direction. */
  direction: RepTestWeightAnswer;
}

/**
 * Everything the app needs to know before it asks a rep-test user why their weight differs from
 * the prescribed one: whether it is off target at all, whether it may ask, and which single answer
 * to offer.
 *
 * `canAsk` is false when there is no real prescribed weight — a path left without a target, which
 * the loader deliberately does not heal for a test set. There is no direction to offer then and
 * asking would have to guess one, so the set is concluded unanswered and `resolveRepTestWeight`
 * reports it rather than reading a direction back at the user.
 */
export function decideRepTestWeightQuestion(loggedWeight_kg: number, prescribedWeight_kg: number): RepTestWeightQuestion {
  const canAsk = typeof prescribedWeight_kg === 'number' && isFinite(prescribedWeight_kg);
  return {
    weightWasOffTarget: isRepTestWeightOffTarget(loggedWeight_kg, prescribedWeight_kg),
    canAsk,
    direction: repTestWeightAnswerForLoggedWeight(loggedWeight_kg, prescribedWeight_kg),
  };
}

/** What concluding a rep test should do about the weight it ran at. */
export interface RepTestWeightVerdict {
  /** whether the set counts as having used the right weight, and so earns the success path */
  weightCorrect: boolean;
  /** the post-set status to record, when the answer records one */
  postStatus?: PostSetStatus;
  /** whether next session must re-test the user's strength, because the user says the
   *  prescription itself was wrong */
  requireStrengthRetest: boolean;
  /** whether the app should offer to configure the equipment to what the user actually used */
  configureWeight: boolean;
  /** the set is off target and nobody asked why. Only reachable when the caller never ran the
   *  question at all, so the caller reports it rather than silently recording a plain failure the
   *  user was never asked about. */
  answerMissing: boolean;
}

/**
 * What a finished rep test means for the weight it ran at, given whether the weight was off target
 * and what the user answered. Pure, so the combinations can be pinned by unit tests — the two that
 * matter most ("off target, nobody answered" and "closest available still counts") were previously
 * reachable only by driving the whole workout page.
 *
 * `weightWasOffTarget` is decided ONCE, by whoever asked the question, and travels here with the
 * answer. Deciding it again off a weight worked out differently is how a user ends up asked about
 * a weight they never changed, or recorded as failing a set nobody questioned.
 *
 * A correction to an already-finished set asks nothing, so `inEditMode` asks nobody and records
 * nothing. It is NOT judged from the weight alone, though: `recordedAnswer` carries what the user
 * answered the first time, read back off the set, and an answer of "this was the closest weight I
 * could get" means the set counted then and counts now. Without that, correcting a typo in the rep
 * count turned a set the user had explained into a failure and took back the rep target it had
 * earned, with nothing saying so (df-6c87ae2df081).
 *
 * What an edit-mode conclusion still does NOT do, even with that answer in hand:
 * - it records no status, because the one it would record is already on the set;
 * - it asks for no equipment configuration, because that offer was made and answered at the time;
 * - it demands no strength retest — see the caller for why that half is left alone.
 */
export function resolveRepTestWeight(args: {
  weightWasOffTarget: boolean;
  answer: RepTestWeightAnswer | undefined;
  inEditMode: boolean;
  /** what the user answered when this set was first concluded, read back off the set's own
   *  recorded statuses. Only consulted in edit mode, where nobody is asked. */
  recordedAnswer: RepTestWeightAnswer | undefined;
}): RepTestWeightVerdict {
  const settled: RepTestWeightVerdict = {
    weightCorrect: !args.weightWasOffTarget,
    requireStrengthRetest: false,
    configureWeight: false,
    answerMissing: false,
  };
  // if the weight was on target, nothing was ever asked about it
  if (!args.weightWasOffTarget) {
    return settled;
  }
  // if this is a correction, honour the answer the user already gave rather than asking again
  if (args.inEditMode) {
    // the user said nothing closer was available, so the set counted then and still counts
    if (args.recordedAnswer === RepTestWeightAnswer.ClosestAvailable) {
      return { ...settled, weightCorrect: true };
    }
    return settled;
  }
  if (args.answer === undefined) {
    return { ...settled, answerMissing: true };
  }
  // the user says nothing closer to the prescription was available, so the set counts
  if (args.answer === RepTestWeightAnswer.ClosestAvailable) {
    return {
      ...settled,
      weightCorrect: true,
      postStatus: postSetStatusForRepTestWeightAnswer(args.answer),
      configureWeight: true,
    };
  }
  // the user says the prescription itself was wrong, so strength is re-tested next session
  return {
    ...settled,
    postStatus: postSetStatusForRepTestWeightAnswer(args.answer),
    requireStrengthRetest: true,
  };
}
